import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PartitionOutlined } from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

/**
 * زیربخش‌های هر بخش (مثلاً پشتیبانی ← فنی، مالی). اپراتور در صندوق، گفتگو را با زیربخش دسته‌بندی می‌کند.
 * دسترسی اپراتورها همچنان در سطح بخش تعریف می‌شود.
 */
const SubDepartmentsCard = () => {
  const [subDepartments, setSubDepartments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, deps] = await Promise.all([liveChatApi.getSubDepartments(), liveChatApi.getDepartments()]);
      setSubDepartments(subs?.data ?? []);
      setDepartments(deps?.data ?? []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت زیربخش‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () => (departmentFilter ? subDepartments.filter((s) => s.department_id === departmentFilter) : subDepartments),
    [subDepartments, departmentFilter],
  );

  const departmentOptions = departments.map((d) => ({
    value: d.id,
    label: d.is_active ? d.name : `${d.name} (غیرفعال)`,
  }));

  const openModal = (sub = null) => {
    setModal({ open: true, editing: sub });
    form.resetFields();
    form.setFieldsValue(
      sub
        ? {
            department_id: sub.department_id,
            name: sub.name,
            description: sub.description,
            is_active: sub.is_active,
            sort_order: sub.sort_order,
          }
        : { is_active: true, department_id: departmentFilter },
    );
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (modal.editing) await liveChatApi.updateSubDepartment(modal.editing.id, values);
      else await liveChatApi.createSubDepartment(values);
      message.success('زیربخش ذخیره شد');
      setModal({ open: false, editing: null });
      load();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره زیربخش');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (sub, isActive) => {
    try {
      await liveChatApi.updateSubDepartment(sub.id, { is_active: isActive });
      setSubDepartments((prev) => prev.map((s) => (s.id === sub.id ? { ...s, is_active: isActive } : s)));
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    }
  };

  const remove = async (sub) => {
    try {
      await liveChatApi.removeSubDepartment(sub.id);
      setSubDepartments((prev) => prev.filter((s) => s.id !== sub.id));
      message.success('زیربخش حذف شد');
    } catch (error) {
      message.error(error?.message || 'خطا در حذف زیربخش');
    }
  };

  const columns = [
    {
      title: 'زیربخش',
      key: 'name',
      render: (_, s) => (
        <div>
          <div className="font-medium custom-font">{s.name}</div>
          {s.description && <div className="text-xs text-gray-500 custom-font">{s.description}</div>}
        </div>
      ),
    },
    {
      title: 'بخش',
      key: 'department',
      render: (_, s) => <Tag color="geekblue" className="custom-font">{s.department?.name ?? '—'}</Tag>,
    },
    {
      title: 'گفتگوی باز',
      dataIndex: 'open_conversations_count',
      key: 'open',
      width: 95,
      render: (v) => Number(v || 0).toLocaleString('fa-IR'),
    },
    { title: 'ترتیب', dataIndex: 'sort_order', key: 'sort_order', width: 70 },
    {
      title: 'فعال',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 70,
      render: (active, s) => <Switch size="small" checked={active} onChange={(v) => toggle(s, v)} />,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, s) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openModal(s)} aria-label="ویرایش" />
          <Popconfirm title="این زیربخش حذف شود؟" description="گفتگوهای آن بدون زیربخش می‌شوند." okText="بله" cancelText="خیر" onConfirm={() => remove(s)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} aria-label="حذف" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="livechat-panel p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="font-bold text-gray-800 dark:text-gray-100 custom-font flex items-center gap-2">
            <PartitionOutlined className="text-brand-primary" /> زیربخش‌ها
          </div>
          <div className="text-xs text-gray-500 custom-font mt-1">
            هر بخش می‌تواند زیربخش داشته باشد (مثلاً پشتیبانی ← فنی، مالی). اپراتور در صندوق گفتگو، زیربخش را برای دسته‌بندی گفتگو تعیین می‌کند.
          </div>
        </div>
        <Space wrap>
          <Select
            allowClear
            placeholder="همه بخش‌ها"
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={departmentOptions}
            className="custom-font"
            style={{ width: 170 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
            disabled={!departments.length}
            className={PANEL_PRIMARY_BTN_CLASS}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن زیربخش
          </Button>
        </Space>
      </div>
      <Spin spinning={loading}>
        {!loading && !departments.length ? (
          <Empty className="py-6" description={<span className="custom-font">ابتدا در تب «بخش‌ها» یک بخش تعریف کنید</span>} />
        ) : rows.length === 0 && !loading ? (
          <Empty className="py-6" description={<span className="custom-font">هنوز زیربخشی تعریف نشده</span>} />
        ) : (
          <Table rowKey="id" size="small" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 560 }} />
        )}
      </Spin>

      <Modal
        title={modal.editing ? 'ویرایش زیربخش' : 'افزودن زیربخش'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        destroyOnHidden
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={form} layout="vertical" className="custom-font">
          <Form.Item
            name="department_id"
            label="بخش"
            rules={[{ required: true, message: 'بخش را انتخاب کنید' }]}
            extra={modal.editing ? 'با انتقال به بخش دیگر، زیربخش از گفتگوهای بخش قبلی برداشته می‌شود.' : undefined}
          >
            <Select options={departmentOptions} placeholder="انتخاب بخش" />
          </Form.Item>
          <Form.Item name="name" label="نام زیربخش" rules={[{ required: true, message: 'نام زیربخش را وارد کنید' }]}>
            <Input maxLength={100} placeholder="مثلاً: پشتیبانی فنی" />
          </Form.Item>
          <Form.Item name="description" label="توضیح (داخلی)">
            <Input maxLength={255} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="sort_order" label="ترتیب نمایش">
              <InputNumber min={0} max={1000} className="w-full" />
            </Form.Item>
            <Form.Item name="is_active" label="فعال" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SubDepartmentsCard;
